<script lang="ts">
  import LoadMore from '$components/directory/load-more.svelte';
  import { t } from '$lib/i18n';
  import { defaultLang, lang } from '$lib/lang';
  import { getStoryblok } from '$lib/storyblok';
  import type { ShopTheLookIndexStoryblok } from '$types/bloks';
  import type { ISbResult } from '@storyblok/js';
  import { createInfiniteQuery } from '@tanstack/svelte-query';
  import { AnalyticsEvents, analyticsEvent } from '$lib/analytics';
  import { getUUID } from '$lib/utils/uuid';
  import { cn } from '$lib/utils';
  import LookDrawer from '$components/look-drawer.svelte';
  import { page } from '$app/stores';
  import Look from '$components/look.svelte';

  export let block: ShopTheLookIndexStoryblok;

  let pageNumber = 1;
  let stylesStories: Array<ISbResult['data']['stories']> = [];
  let stylesData: Array<ISbResult['data']['stories']> = [];

  const storyblok = getStoryblok();
  const drawerId = 'styled-by-you' + getUUID();

  $: isDraft = $page.data.version === 'draft';

  const fetchStories = async ({ pageParam }: { pageParam?: number | undefined }) => {
    return await storyblok.get('cdn/stories/', {
      content_type: 'styled-by-you',
      page: pageParam,
      per_page: Number(block.items_per_page) || 6,
      cv: Date.now(),
      resolve_relations: 'styled-by-you.products',
      version: isDraft ? 'draft' : 'published'
    });
  };

  $: client = createInfiniteQuery({
    staleTime: 5 * 60 * 1000,
    queryKey: ['styled-by-you-index', $lang],
    queryFn: fetchStories,
    getNextPageParam: (lastPage) => {
      if (lastPage.data && lastPage.data.stories && lastPage.data.stories.length > 0) {
        const totalPages = Math.ceil(lastPage.total / lastPage.perPage);

        if (totalPages > pageNumber) {
          const nextUrl = new URLSearchParams(`?page=${pageNumber + 1}`);
          const nextCursor = nextUrl.get('page');

          if (nextCursor) {
            return +nextCursor;
          }
        }

        return undefined;
      }
      return undefined;
    }
  });

  $: if ($client.data?.pages) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    stylesStories = ($client.data.pages || []).reduce((a: Array<any>, b) => {
      return [...a, ...(b.data.stories || [])];
    }, []);

    stylesData = stylesStories.map((story) => {
      return {
        markers: story.content.markers,
        username: story.content.username,
        image: story.content.image?.filename,
        imageAsset: story.content.image,
        products: [...(story.content.products_with_links || []), ...(story.content.products || [])],
        date: story.content.date,
        caption: story.content.caption,
        hideDate: story.content.hide_date,
        video: story.content.video,
        avatar: story.content.avatar,
        event_id: story.name
      };
    });
  }

  $: total = $client?.data?.pages[0].total || 0;
  $: showing = ($client?.data?.pages || []).reduce((a, b) => a + (b.data.stories.length || 0), 0);

  $: looks = stylesData.map((style) => {
    return {
      markers: style.markers,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      products: style.products?.map((product: any) => {
        return { handle: product.slug, link: product.link };
      }),
      video: style.video,
      image: style.image,
      name: style.username,
      avatar: style.avatar,
      username: style.username,
      date: style.date,
      caption: style.caption,
      event_id: style.event_id
    };
  });
</script>

<section class="overflow-hidden py-12 px-4 lg:px-8">
  <div class="container mx-auto">
    <h2 class="text-5xl font-semibold mb-4">{block.title}</h2>
    <p class="text-xl max-w-2xl">{block.subtitle}</p>

    <p
      class={cn('text-md text-primary/40 mb-4', stylesStories.length > 0 ? 'visible' : 'invisible')}
    >
      {total}
      {$t('styled-by-you.styles')}
    </p>

    <div class="flex gap-2 sm:gap-4 flex-wrap flex-col sm:flex-row">
      {#if stylesStories}
        {#each looks as look, i}
          <Look
            {look}
            {drawerId}
            class={cn('w-full lg:w-1/4 flex-grow')}
            index={i}
            on:click={() => {
              analyticsEvent({
                event: AnalyticsEvents.OPEN_STYLED_BY_YOU,
                username: look.username || '',
                date: look.date
                  ? new Date(look.date).toLocaleDateString(defaultLang, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })
                  : undefined
              });
            }}
          />
        {/each}
        <LookDrawer allLooks={looks} {drawerId} />
      {/if}
    </div>
    <LoadMore
      {total}
      {showing}
      loading={$client.isFetchingNextPage}
      hasNextPage={$client.hasNextPage}
      on:loadMore={() => {
        $client.fetchNextPage();
        pageNumber += 1;
      }}
    />
  </div>
</section>
